import { tokenGraph } from './login.js'

/// Busca de pessoas no diretório da Ápia (Entra ID), pela Microsoft Graph.
///
/// Roda no navegador, com a permissão de quem está usando a tela, e não no
/// servidor: assim o servidor não precisa de segredo nem de permissão própria para
/// ler o diretório, e ninguém vê pela tela mais do que já veria no Outlook.
///
/// Permissão pedida: `User.ReadBasic.All` (nome, e-mail e id de todo mundo). Precisa
/// estar no registro do aplicativo, com consentimento — sem ela, a Graph responde
/// 403 e a tela diz o que falta.

const ESCOPOS = ['User.ReadBasic.All']

export interface PessoaDiretorio {
  /// Id do objeto no Entra ID. É o que o login confere primeiro, então o cadastro
  /// fica ligado à conta mesmo que o e-mail seja digitado de outro jeito.
  oid: string
  nome: string
  email: string
  /// Convidado de outra empresa (consultor, parceiro).
  convidado: boolean
}

export class SemPermissaoDiretorio extends Error {}

export async function buscarNoDiretorio(termo: string, interativo = false): Promise<PessoaDiretorio[]> {
  const token = await tokenGraph(ESCOPOS, interativo)
  // Aspas fecham a expressão de busca da Graph; tirar é mais seguro que escapar.
  const limpo = termo.replace(/["\\]/g, '').trim()
  const expressao = `"displayName:${limpo}" OR "mail:${limpo}" OR "userPrincipalName:${limpo}"`
  const url =
    'https://graph.microsoft.com/v1.0/users' +
    `?$search=${encodeURIComponent(expressao)}` +
    '&$select=id,displayName,mail,userPrincipalName&$top=15'

  let resposta: Response
  try {
    // `ConsistencyLevel: eventual` é exigido pela Graph para usar $search.
    resposta = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, ConsistencyLevel: 'eventual' },
    })
  } catch {
    throw new Error('Sem conexão com a Microsoft.')
  }
  if (resposta.status === 401 || resposta.status === 403) {
    throw new SemPermissaoDiretorio(
      'O aplicativo ainda não tem permissão para ler o diretório (User.ReadBasic.All). Procure a TI.',
    )
  }
  if (!resposta.ok) throw new Error(`A Microsoft respondeu com erro ${resposta.status}.`)

  const corpo = (await resposta.json()) as {
    value: { id: string; displayName: string | null; mail: string | null; userPrincipalName: string | null }[]
  }
  return corpo.value
    .map((u) => {
      const upn = u.userPrincipalName ?? ''
      // O convidado tem um UPN artificial (fulano_empresa.com#EXT#@...). O e-mail
      // de verdade dele está em `mail`. Para os da casa, vale o UPN: é o que o
      // login da Microsoft informa.
      const convidado = upn.includes('#EXT#')
      const email = (convidado ? u.mail ?? upn : upn || u.mail || '').toLowerCase()
      return { oid: u.id, nome: (u.displayName ?? email).trim(), email, convidado }
    })
    .filter((p) => p.email)
    .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
}

/// Abre a janela da Microsoft para liberar a busca (consentimento ou sessão
/// vencida). Precisa ser chamada direto de um clique.
export async function conectarDiretorio(): Promise<void> {
  await tokenGraph(ESCOPOS, true)
}
